import React, { useState, useEffect, useCallback } from 'react';
import { FiGlobe, FiClock, FiActivity, FiAlertCircle, FiSettings, FiChevronDown, FiChevronUp, FiRefreshCw } from 'react-icons/fi';
import Flag from 'react-world-flags';
import ExchangeSettingsPanel from './ExchangeSettingsPanel';
import { services } from '../../../../services/api';
import useWebSocket from '../../../../hooks/useWebSocket';
import WebSocketManager from '../../../../websocket/WebSocketManager';
import { useExchangeStatusUpdate } from '../../../../context/WebSocketProvider';
import Loader from "../../../../components/Loader";

const MarketStatusDashboard = () => {
    const [showSettings, setShowSettings] = useState(false);
    const [markets, setMarkets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Fetch market status from API
    const fetchMarketStatus = useCallback(async (isManualRefresh = false) => {
        // Helper to get timezone abbreviation
        const getTimezoneForCountry = (country) => {
            const tzMap = {
                'India': 'IST',
                'United States': 'EST/EDT',
                'United Kingdom': 'GMT/BST',
                'Japan': 'JST',
                'Singapore': 'SGT',
                'Germany': 'CET/CEST',
                'France': 'CET/CEST',
                'China': 'CST',
                'Hong Kong': 'HKT',
                'Australia': 'AEST/AEDT',
                'Canada': 'EST/EDT',
                'South Korea': 'KST'
            };
            return tzMap[country] || 'UTC';
        };

        const getCountryCode = (country) => {
            const codeMap = {
                'India': 'IN',
                'United States': 'US',
                'United Kingdom': 'GB',
                'Japan': 'JP',
                'Singapore': 'SG',
                'Germany': 'DE',
                'France': 'FR',
                'China': 'CN',
                'Australia': 'AU',
                'Hong Kong': 'HK',
                'Canada': 'CA',
                'South Korea': 'KR'
            };
            return codeMap[country] || null;
        };

        // Transform flat exchanges object to grouped markets array
        const transformToMarketGroups = (exchanges) => {
            const groups = {};
            
            Object.entries(exchanges).forEach(([code, exchange]) => {
                // Parse the exchange data (may be JSON string from Redis)
                const exData = typeof exchange === 'string' ? JSON.parse(exchange) : exchange;
                
                const country = exData.country || 'Unknown';
                const flag = exData.flag || '🌐';
                
                if (!groups[country]) {
                    groups[country] = {
                        country,
                        flag,
                        countryCode: getCountryCode(country),
                        timezone: getTimezoneForCountry(country),
                        exchanges: []
                    };
                }
                
                // Calculate countdown using timezone from backend
                const calculateCountdown = (tradingHours, isOpen, timezone) => {
                    if (!tradingHours || tradingHours === '--:-- - --:--' || !timezone) return null;
                    
                    try {
                        const [openTime, closeTime] = tradingHours.split(' - ');
                        const [openH, openM] = openTime.split(':').map(Number);
                        const [closeH, closeM] = closeTime.split(':').map(Number);
                        
                        // Get current time in the exchange timezone
                        const nowInExchangeTz = new Date(new Intl.DateTimeFormat('en-US', {
                            timeZone: timezone,
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false
                        }).format(new Date()));
                        
                        // Create today's open/close times in exchange timezone
                        const todayOpen = new Date(nowInExchangeTz);
                        todayOpen.setHours(openH, openM, 0, 0);
                        
                        const todayClose = new Date(nowInExchangeTz);
                        todayClose.setHours(closeH, closeM, 0, 0);
                        
                        let diffMs;
                        if (isOpen) {
                            // Market is open - count down to close
                            if (nowInExchangeTz < todayClose) {
                                diffMs = todayClose - nowInExchangeTz;
                            } else {
                                return null; // Should be closed
                            }
                        } else {
                            // Market is closed - count down to open
                            if (nowInExchangeTz < todayOpen) {
                                // Opens later today
                                diffMs = todayOpen - nowInExchangeTz;
                            } else {
                                // Opens tomorrow
                                const tomorrowOpen = new Date(todayOpen);
                                tomorrowOpen.setDate(tomorrowOpen.getDate() + 1);
                                diffMs = tomorrowOpen - nowInExchangeTz;
                            }
                        }
                        
                        // Format countdown
                        const hours = Math.floor(diffMs / (1000 * 60 * 60));
                        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        
                        if (hours >= 24) {
                            return `${Math.floor(hours / 24)}d ${hours % 24}h`;
                        } else if (hours > 0) {
                            return `${hours}h ${minutes}m`;
                        } else {
                            return `${minutes}m`;
                        }
                    } catch (e) {
                        console.error("Countdown error:", e);
                        return null;
                    }
                };
                
                const isOpen = exData.is_open;
                const tradingHours = exData.trading_hours || '--:-- - --:--';
                const currentTime = exData.current_time || '--:--';
                const timezone = exData.timezone || 'UTC';
                const countdown = calculateCountdown(tradingHours, isOpen, timezone);
                
                groups[country].exchanges.push({
                    name: exData.exchange || code,
                    fullName: exData.full_name || code,
                    status: isOpen ? 'open' : 'closed',
                    time: tradingHours,
                    current: currentTime,
                    timezone: timezone,
                    validationSource: exData.validation_source || 'unknown',
                    //! exchange-session-gate-entry-and-live-status R3-T3 — config == runtime
                    //! observable. No new fetch; reads fields already in the payload.
                    configDegraded: exData.config_degraded ?? false,
                    description: exData.description,
                    countdown: countdown,
                    isOpen: isOpen,
                    isHoliday: exData.is_holiday,
                    isPartialHoliday: exData.is_partial_holiday,
                    holidayReason: exData.holiday_reason,
                    //! #31/exchange-session-ssot R2: pre-deploy records (≤1 writer
                    //! cadence after an MDMS restart) lack this key entirely — `?? true`
                    //! matches the backend's own D14 fail-open default (C-12).
                    hoursVerified: exData.hours_verified ?? true
                });
            });
            
            // Sort: India first, then USA, then others alphabetically
            const sortOrder = { 'India': 0, 'United States': 1 };
            return Object.values(groups).sort((a, b) => {
                const orderA = sortOrder[a.country] ?? 99;
                const orderB = sortOrder[b.country] ?? 99;
                if (orderA !== orderB) return orderA - orderB;
                return a.country.localeCompare(b.country);
            });
        };

        try {
            if (isManualRefresh) {
                setRefreshing(true);
            }
            setError(null);
            
            const response = await services.exchangeStatus.getAllStatus();
            const data = response.data;
            
            if (data.success && data.exchanges) {
                // Transform API response to grouped markets format
                const groupedMarkets = transformToMarketGroups(data.exchanges);
                setMarkets(groupedMarkets);
                setLastUpdated(new Date());
            } else {
                console.error('Invalid API response:', data);
                setError('Invalid response from server');
            }
        } catch (err) {
            console.error('Failed to fetch market status:', err);
            setError('Failed to load market data. Please check your connection.');
            //! #31/exchange-session-ssot DK-4: MDMS unreachable must not silently
            //! keep showing a stale open/closed badge for a venue we can no longer
            //! confirm. Degrade every already-known venue row to an explicit grey
            //! 'unknown' status (banner stays up) instead of leaving `markets`
            //! untouched. Never fabricate hours/current-time/hours_verified for
            //! that row. A venue this dashboard has never successfully loaded has
            //! no identity to show and stays absent — there is no frontend copy of
            //! MONITORED_EXCHANGES to enumerate from (R1.2 single-locus, backend-only).
            //! "the row shows unknown, nothing more" — every field derived from a
            //! confirmed status (hours, current time, countdown, hours_verified,
            //! holiday info, validation source) is cleared, not just the badge.
            //! Only static venue identity (name/fullName/timezone) survives.
            setMarkets(prevMarkets => prevMarkets.map(market => ({
                ...market,
                exchanges: market.exchanges.map(ex => ({
                    ...ex,
                    status: 'unknown',
                    time: '--:-- - --:--',
                    current: '--:--',
                    countdown: null,
                    isOpen: false,
                    hoursVerified: undefined,
                    isHoliday: undefined,
                    isPartialHoliday: undefined,
                    holidayReason: undefined,
                    validationSource: 'unknown',
                })),
            })));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);


    // WebSocket for real-time updates
    const { isConnected } = useWebSocket();
    //! #31/exchange-session-ssot R5.2 (DK-4): the local `exchange-status.update`
    //! subscription that used to live here is DELETED — WebSocketProvider now owns
    //! the ONE subscription for the whole app and fans it out via context.
    const exchangeStatusUpdate = useExchangeStatusUpdate();

    // Initial load
    useEffect(() => {
        fetchMarketStatus();
    }, [fetchMarketStatus]);

    // Refresh data when subscriptions resume from background
    useEffect(() => {
        const handleResume = (data) => {
            console.log('[MarketStatusDashboard] Subscriptions resumed after background. Duration:', data.backgroundDuration, 'ms');
            
            // Refresh data if we were in background for more than 30 seconds
            if (data.backgroundDuration > 30000) {
                console.log('[MarketStatusDashboard] Refreshing data after long background period');
                fetchMarketStatus();
            }
        };

        // Listen to WebSocket manager's resume event
        WebSocketManager.on('tab_foregrounded', handleResume);

        return () => {
            WebSocketManager.off('tab_foregrounded', handleResume);
        };
    }, [fetchMarketStatus]);

    // Apply real-time exchange status updates fanned out by WebSocketProvider's
    // single `exchange-status.update` subscription (#31/exchange-session-ssot R5.2).
    useEffect(() => {
        const data = exchangeStatusUpdate;
        if (!data || !data.exchange) return;

        console.log('📡 [MarketStatusDashboard] Applying exchange status update:', data);
        setMarkets(prevMarkets => {
            return prevMarkets.map(market => ({
                ...market,
                exchanges: market.exchanges.map(ex => {
                    if (ex.name === data.exchange) {
                        // Calculate countdown using same timezone-aware logic
                        const tradingHours = data.trading_hours || '--:-- - --:--';
                        const isOpen = data.is_open;
                        const currentTime = data.current_time || '--:--';
                        const timezone = data.timezone || ex.timezone || 'UTC';
                        let countdown = null;

                        try {
                            if (tradingHours !== '--:-- - --:--' && timezone) {
                                const [openTime, closeTime] = tradingHours.split(' - ');
                                const [openH, openM] = openTime.split(':').map(Number);
                                const [closeH, closeM] = closeTime.split(':').map(Number);

                                const nowInExchangeTz = new Date(new Intl.DateTimeFormat('en-US', {
                                    timeZone: timezone,
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false
                                }).format(new Date()));

                                const todayOpen = new Date(nowInExchangeTz);
                                todayOpen.setHours(openH, openM, 0, 0);
                                const todayClose = new Date(nowInExchangeTz);
                                todayClose.setHours(closeH, closeM, 0, 0);

                                let diffMs;
                                if (isOpen) {
                                   if (nowInExchangeTz < todayClose) diffMs = todayClose - nowInExchangeTz;
                                } else {
                                   if (nowInExchangeTz < todayOpen) diffMs = todayOpen - nowInExchangeTz;
                                   else {
                                       const tomorrowOpen = new Date(todayOpen);
                                       tomorrowOpen.setDate(tomorrowOpen.getDate() + 1);
                                       diffMs = tomorrowOpen - nowInExchangeTz;
                                   }
                                }

                                if (diffMs !== undefined) {
                                    const hours = Math.floor(diffMs / (1000 * 60 * 60));
                                    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                                    if (hours >= 24) countdown = `${Math.floor(hours/24)}d ${hours%24}h`;
                                    else if (hours > 0) countdown = `${hours}h ${minutes}m`;
                                    else countdown = `${minutes}m`;
                                }
                            }
                        } catch (e) { console.error('Countdown calc error:', e); }

                        return {
                            ...ex,
                            status: isOpen ? 'open' : 'closed',
                            current: currentTime,
                            time: tradingHours,
                            timezone: timezone,
                            validationSource: data.validation_source || ex.validationSource,
                            configDegraded: data.config_degraded ?? ex.configDegraded,
                            countdown,
                            isOpen,
                            isHoliday: data.is_holiday !== undefined ? data.is_holiday : ex.isHoliday,
                            holidayReason: data.holiday_reason || ex.holidayReason,
                            hoursVerified: data.hours_verified ?? ex.hoursVerified
                        };
                    }
                    return ex;
                })
            }));
        });
        setLastUpdated(new Date());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exchangeStatusUpdate]);

    // Live clock update - runs every second to update current time and countdowns
    useEffect(() => {
        const updateTimes = () => {
            setMarkets(prevMarkets => {
                return prevMarkets.map(market => ({
                    ...market,
                    exchanges: market.exchanges.map(ex => {
                        // Skip if no timezone info (should have it now)
                        if (!ex.timezone) return ex;

                        try {
                            const now = new Date();
                            
                            // 1. Get current time in exchange timezone
                            const timeFormatter = new Intl.DateTimeFormat('en-US', {
                                timeZone: ex.timezone,
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                            });
                            
                            // Handle formatting to ensure HH:MM:SS format
                            const parts = timeFormatter.formatToParts(now);
                            const getPart = (type) => parts.find(p => p.type === type)?.value || '00';
                            const currentHStr = getPart('hour');
                            const currentMStr = getPart('minute');
                            const currentSStr = getPart('second');
                            const currentTime = `${currentHStr}:${currentMStr}:${currentSStr}`;

                            // 2. Calculate countdown (logic duplicated for independence)
                            let countdown = null;
                            const tradingHours = ex.time;
                            
                            if (tradingHours && tradingHours !== '--:-- - --:--') {
                                const [openTime, closeTime] = tradingHours.split(' - ');
                                const [openH, openM] = openTime.split(':').map(Number);
                                const [closeH, closeM] = closeTime.split(':').map(Number);
                                
                                // Create Date objects in exchange timezone for accurate comparison
                                // We construct "now" in exchange TZ by using the formatted parts
                                const nowInExchangeTz = new Date(now.toLocaleString('en-US', { timeZone: ex.timezone }));
                                
                                const todayOpen = new Date(nowInExchangeTz);
                                todayOpen.setHours(openH, openM, 0, 0);
                                
                                const todayClose = new Date(nowInExchangeTz);
                                todayClose.setHours(closeH, closeM, 0, 0);
                                
                                let diffMs;
                                if (ex.isOpen) {
                                    // Market is open - counting down to close
                                    if (nowInExchangeTz < todayClose) {
                                        diffMs = todayClose - nowInExchangeTz;
                                    }
                                } else {
                                    // Market is closed - counting down to open
                                    if (nowInExchangeTz < todayOpen) {
                                        diffMs = todayOpen - nowInExchangeTz;
                                    } else {
                                        // Opens tomorrow
                                        const tomorrowOpen = new Date(todayOpen);
                                        tomorrowOpen.setDate(tomorrowOpen.getDate() + 1);
                                        diffMs = tomorrowOpen - nowInExchangeTz;
                                    }
                                }
                                
                                if (diffMs && diffMs > 0) {
                                    const hours = Math.floor(diffMs / (1000 * 60 * 60));
                                    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                                    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
                                    
                                    if (hours >= 24) {
                                        countdown = `${Math.floor(hours / 24)}d ${hours % 24}h`;
                                    } else if (hours > 0) {
                                        countdown = `${hours}h ${minutes}m`;
                                    } else {
                                        // Show seconds if less than 1 hour for liveness
                                        countdown = `${minutes}m ${seconds}s`;
                                    }
                                }
                            }

                            return {
                                ...ex,
                                current: currentTime,
                                countdown: countdown || ex.countdown
                            };
                        } catch (e) {
                            return ex;
                        }
                    })
                }));
            });
        };

        const timer = setInterval(updateTimes, 1000);
        return () => clearInterval(timer);
    }, []);

    // Calculate summary stats
    const totalExchanges = markets.reduce((acc, m) => acc + m.exchanges.length, 0);
    const openExchanges = markets.reduce((acc, m) => 
        acc + m.exchanges.filter(e => e.status === 'open').length, 0
    );

    // --> DS badge variant for an exchange status (colour carries the meaning).
    //! #31/exchange-session-ssot DK-4: 'unknown' is its own explicit state (MDMS
    //! unreachable) — grey/neutral like 'closed', but must stay distinguishable
    //! from a confirmed closed market, which the icon (below) carries.
    const getStatusVariant = (status) => {
        switch (status) {
            case 'open': return 'profit';
            case 'closed': return 'neutral';
            case 'pre-market': return 'warn';
            case 'post-market': return 'info';
            case 'unknown': return 'neutral';
            default: return 'neutral';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'open': return <FiActivity className="animate-pulse" />;
            case 'closed': return <FiClock />;
            case 'pre-market': return <FiAlertCircle />;
            //! grey/dimmed — distinct from the solid FiClock used for a confirmed close.
            case 'unknown': return <FiAlertCircle className="opacity-50" />;
            default: return <FiClock />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center justify-center h-48">
                    <Loader text="Loading market status..." />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Error Banner */}
            {error && (
                <div className="p-4 bg-loss/10 border border-loss/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FiAlertCircle className="text-loss" />
                        <span className="text-sm text-loss">{error}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => fetchMarketStatus(true)}
                        className="btn btn-ghost btn-sm"
                    >
                        Retry
                    </button>
                </div>
            )}
            
            {/* Header Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-profit/10 p-6 rounded-2xl border border-profit/20">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-bg-2 rounded-xl shadow-sm">
                            <FiActivity className="text-profit text-2xl" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-profit uppercase tracking-wide">Open Markets</p>
                            <h3 className="ds-h3 text-fg-1">{openExchanges} / {totalExchanges}</h3>
                        </div>
                    </div>
                </div>
                
                <div className="bg-accent/10 p-6 rounded-2xl border border-accent/20">
                     <div className="flex items-center gap-4">
                        <div className="p-3 bg-bg-2 rounded-xl shadow-sm">
                            <FiGlobe className="text-accent text-2xl" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-accent uppercase tracking-wide">Regions</p>
                            <h3 className="ds-h3 text-fg-1">{markets.length} Countries</h3>
                        </div>
                    </div>
                </div>

                <div className="bg-bg-3 p-6 rounded-2xl border border-[var(--divider)]">
                     <div className="flex items-center gap-4">
                        <div className="p-3 bg-bg-2 rounded-xl shadow-sm relative">
                            <FiClock className="text-fg-2 text-2xl" />
                            {isConnected && (
                                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-profit opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-profit"></span>
                                </span>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-fg-2 uppercase tracking-wide">Last Updated</p>
                                {isConnected && <span className="ds-badge xs sq profit">LIVE</span>}
                            </div>
                            <h3 className="ds-h3 text-fg-1">
                                {lastUpdated ? lastUpdated.toLocaleTimeString() : '--:--'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => fetchMarketStatus(true)}
                                disabled={refreshing}
                                className="btn btn-ghost btn-sm mt-1 flex items-center gap-1"
                            >
                                <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
                                {refreshing ? 'Refreshing...' : 'Force Refresh'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Market Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                {markets.map((market, idx) => (
                    <div key={idx} className="bg-bg-2 rounded-2xl shadow-sm border border-[var(--divider)] overflow-hidden hover:shadow-md transition-shadow duration-300">
                        {/* Country Header */}
                        <div className="px-6 py-4 border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/50 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {market.countryCode ? (
                                    <Flag 
                                        code={market.countryCode} 
                                        className="w-8 h-6 shadow-sm rounded-sm object-cover"
                                        title={market.country}
                                    />
                                ) : (
                                    <span className="text-2xl" role="img" aria-label={market.country}>{market.flag}</span>
                                )}
                                <div>
                                    <h3 className="font-bold text-fg-1">{market.country}</h3>
                                    <p className="text-xs text-fg-2 font-mono tracking-wider">{market.timezone}</p>
                                </div>
                            </div>
                        </div>

                        {/* Exchanges List */}
                        <div className="p-4 space-y-3">
                            {market.exchanges.map((ex, exIdx) => (
                                /* Stacks below sm: the identity block and the time block were
                                   splitting ~287px between them, so a long exchange name and
                                   "Partial Session Today" fought over the same row. */
                                <div key={exIdx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl bg-bg-3/30 border border-[var(--divider)]">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center flex-wrap gap-2">
                                            <span className="font-bold text-fg-1">{ex.name}</span>
                                            <span className={`ds-badge xs uppercase ${getStatusVariant(ex.status)}`}>
                                                {getStatusIcon(ex.status)}
                                                {ex.status}
                                            </span>
                                            
                                            {/* Holiday Reason - Subtle & Inline */}
                                            {ex.isHoliday && ex.holidayReason && (
                                                <div className="flex items-center gap-1.5 pl-2 border-l border-[var(--divider)] ml-1">
                                                    <span className="text-[10px] text-fg-3 font-medium hidden sm:inline">Holiday:</span>
                                                    <span className="ds-badge xs warn max-w-[150px] sm:max-w-none truncate" title={ex.holidayReason}>
                                                        🎉 {ex.holidayReason}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <p className="text-xs text-fg-2 mt-1 truncate max-w-full sm:max-w-[180px]" title={ex.fullName}>{ex.fullName}</p>
                                        
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {/* #31/exchange-session-ssot DK-4: an 'unknown' row has no
                                                confirmed validation source — showing "📅 Calendar" (the
                                                default branch) would fabricate one. Suppress entirely. */}
                                            {ex.status !== 'unknown' && (
                                                <span className={`ds-badge xs sq ${
                                                    ex.validationSource === 'observed'
                                                        ? 'profit'
                                                        : ex.validationSource === 'broker'
                                                        ? 'accent'
                                                        : ex.validationSource === 'global_api'
                                                        ? 'info'
                                                        : 'neutral'
                                                }`} title={`Status validated via ${ex.validationSource}`}>
                                                    {/* exchange-session-gate-entry-and-live-status R3 —
                                                        'observed' = MDMS's own bulk freshness fold
                                                        (fresh ticks inside scheduled hours). */}
                                                    {ex.validationSource === 'observed' ? '📡 Live data'
                                                        : ex.validationSource === 'broker' ? '🔗 Broker API'
                                                        : ex.validationSource === 'global_api' ? '🌐 Alpha Vantage'
                                                        : '📅 Calendar'}
                                                </span>
                                            )}
                                            {/* exchange-session-gate-entry-and-live-status R3-T3 —
                                                a config-fetch failure is now loud and distinct;
                                                surfaced here so an operator sees the degrade, not
                                                just the silent calendar_only fallback. */}
                                            {ex.configDegraded && (
                                                <span className="ds-badge xs sq warn" title="exchange_validation_config fetch failed — degraded to calendar_only">
                                                    ⚠ Config degraded
                                                </span>
                                            )}
                                            {/* #31/exchange-session-ssot R2/DK-3: hours for this venue are a
                                                repo constant, not broker/calendar-verified (the 9 foreign
                                                venues) — grey, non-alarming, informational only. */}
                                            {ex.hoursVerified === false && (
                                                <span className="ds-badge xs sq neutral" title="Trading hours for this venue are not broker/calendar-verified">
                                                    ⚠️ Hours unverified
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Phone: a full-width strip under the identity (time left,
                                        countdown right). sm+: the original right-aligned column. */}
                                    <div className="flex items-end justify-between gap-3 border-t border-[var(--divider)] pt-2 sm:border-0 sm:pt-0 sm:pl-2 sm:block sm:text-right shrink-0">
                                        <div className="min-w-0">
                                            <p className="text-xs font-medium text-fg-3 uppercase">Exchange Time</p>
                                            <p className="text-sm font-mono font-bold text-fg-2">{ex.current}</p>
                                        </div>
                                        <div className="text-right min-w-0">
                                            {ex.isPartialHoliday && (
                                                <p className="text-[10px] text-warning font-bold mt-0.5" title="Partial Trading Session">
                                                    Session: {ex.time}
                                                </p>
                                            )}
                                            {/* Enhanced messaging for partial holidays before session starts */}
                                            {ex.isPartialHoliday && !ex.isOpen && (
                                                <p className="text-[10px] text-warning font-medium mt-0.5 flex items-center justify-end gap-1">
                                                    <span>⚠️</span>
                                                    <span>Partial Session Today</span>
                                                </p>
                                            )}
                                            {/* Countdown display - dynamically calculated */}
                                            {ex.countdown && (
                                                <p className={`text-xs mt-1 font-medium whitespace-nowrap ${
                                                    ex.isOpen
                                                        ? 'text-profit'
                                                        : 'text-warning'
                                                }`}>
                                                    {ex.isOpen ? `Closes in ${ex.countdown}` : `Opens in ${ex.countdown}`}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Footer Info */}
                        <div className="px-6 py-3 bg-bg-3/30 dark:bg-bg-2/10 border-t border-[var(--divider)] text-xs text-fg-3 text-center flex items-center justify-center gap-2">
                            <span>Real-time data enabled</span>
                            {isConnected ? (
                                <span className="flex items-center gap-1 text-profit font-medium">
                                    <span className="w-1.5 h-1.5 rounded-full bg-profit"></span>
                                    Connected
                                </span>
                            ) : (
                                <span className="flex items-center gap-1 text-warning font-medium">
                                    <span className="w-1.5 h-1.5 rounded-full bg-warning"></span>
                                    Connecting...
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* No Data State */}
            {markets.length === 0 && !loading && !error && (
                <div className="text-center py-12 bg-bg-3/50 rounded-xl">
                    <FiGlobe className="mx-auto text-4xl text-fg-3 mb-4" />
                    <p className="text-fg-2">No market data available</p>
                    <p className="text-sm text-fg-2 mt-2">Make sure the exchange status job is running</p>
                </div>
            )}

            {/* Settings Section */}
            <div className="mt-8">
                <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="w-full flex items-center justify-between p-4 bg-bg-1 hover:bg-bg-3 rounded-lg transition-colors border border-[var(--divider)]"
                >
                    <div className="flex items-center">
                        <FiSettings className="text-xl text-fg-2 mr-3" />
                        <span className="font-medium text-fg-1">
                            Exchange Validation Settings
                        </span>
                        <span className="ml-3 text-sm text-fg-2">
                            (Admin Only)
                        </span>
                    </div>
                    {showSettings ? (
                        <FiChevronUp className="text-fg-2" />
                    ) : (
                        <FiChevronDown className="text-fg-2" />
                    )}
                </button>
                
                {showSettings && (
                    <div className="mt-4">
                        <ExchangeSettingsPanel />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketStatusDashboard;
